import { useState } from 'react';
import {
  useStripe,
  useElements,
  PaymentElement,
} from '@stripe/react-stripe-js';
import { Navigate, useNavigate } from 'react-router';

import StripeProvider from './StripeProvider';
import UpButton from '../../components/UpButton';
import PaymentInfo from '../Commons/PaymentInfo';
import Skeleton from '../../components/Skeleton';
import { formatMoney } from '../../helpers/utils';
import BottomFooter from '../../components/BottomFooter';
import { FailedIcon, ChevronLeftIcon } from '../../assets/Icons';
import type { OrderResponsePayment } from '../../types/codexOrder';

function OrderPayment({
  orderId,
  loading,
  payment,
  orderTotal,
  awaitingPayment,
}: {
  orderId: string;
  loading: boolean;
  awaitingPayment: boolean;
  orderTotal: number | undefined;
  payment: OrderResponsePayment | undefined;
}) {
  if (loading) {
    return <OrderPaymentSkeleton />;
  }

  if (!awaitingPayment || !payment) {
    return <Navigate to={`/${orderId}`} replace />;
  }

  return (
    <StripeProvider
      publishKey={payment?.key}
      clientSecret={payment?.data?.stripe_client_secret}>
      <PaymentUI orderId={orderId} orderTotal={orderTotal} />
    </StripeProvider>
  );
}

function PaymentUI({
  orderId,
  orderTotal,
}: {
  orderId: string;
  orderTotal: number | undefined;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isPaymentReady, setIsPaymentReady] = useState(false);
  const [paymentElementKey, setPaymentElementKey] = useState(0);

  const handleSubmit = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (!stripe || !elements) {
      return;
    }

    try {
      setLoading(true);
      setErrorMessage('');
      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${import.meta.env.CHECKOUT_WEB_BASE_FE_URL}${orderId}/status`,
        },
      });

      if (error?.payment_intent?.status === 'succeeded') {
        void navigate(`/${orderId}/status`);
        return;
      }

      if (error?.message) {
        setErrorMessage(error.message);
      }
    } catch {
      setErrorMessage(
        'Unable to process payment. Please check your connection and try again.',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pb-24">
      <div className="flex items-center gap-x-2 px-4 py-6 bg-brand-background text-brand-text md:rounded-b-lg md:mx-2">
        <ChevronLeftIcon
          className="cursor-pointer"
          onClick={() => {
            void navigate(`/${orderId}`);
          }}
        />
        <p className="text-lg font-bold">Payment Methods</p>
        <p className="text-sm ml-auto">Pay {formatMoney(orderTotal)}</p>
      </div>
      <div className="px-2">
        <p className="font-bold text-sm mt-6 mb-3 pl-4">
          Select a payment method
        </p>
        <PaymentElement
          key={paymentElementKey}
          onChange={event => {
            setIsPaymentReady(event.complete);
          }}
        />
        <PaymentInfo />
      </div>
      {(isPaymentReady || errorMessage) && (
        <BottomFooter>
          {errorMessage ? (
            <div className="mb-1">
              <div className="flex items-start gap-x-3 mb-8 mt-1">
                <FailedIcon className="text-up-red-70" />
                <div>
                  <p className="mb-2 font-bold text-lg">Payment failed!</p>
                  <p className="text-sm font-[500] text-up-black-60 leading-6">
                    {errorMessage} Any amount deducted will be refunded. Please
                    try again or change your payment method.
                  </p>
                </div>
              </div>
              <UpButton
                onClick={() => {
                  setPaymentElementKey(prev => prev + 1);
                  setErrorMessage('');
                }}>
                Retry payment
              </UpButton>
            </div>
          ) : (
            <UpButton
              loading={loading}
              onClick={e => {
                void handleSubmit(e);
              }}>
              Pay now
            </UpButton>
          )}
        </BottomFooter>
      )}
    </div>
  );
}

function OrderPaymentSkeleton() {
  return (
    <div className="px-2 pb-4 pt-20 overflow-x-hidden space-y-4">
      <Skeleton rows={1} />
      <Skeleton rows={1} />
    </div>
  );
}

export default OrderPayment;
